<template>
  <!--
    Panel /admin/home — edicion del documento Firestore siteConfig/home.
    Toda la configuracion del home rediseñado (brief 2026-05-17) se controla
    desde aca: top promo bar, hero, visibilidad y orden de secciones,
    contenidos de cada seccion, meta SEO.

    Estructura: secciones colapsables, un boton "Guardar" sticky al pie.

    Importante: Pablo debe saber que los cambios se ven en produccion
    DESPUES del proximo rebuild de Vercel (1-3 min) — el sitio publico es
    SSG (target:static) y lee siteConfig/home en build time. El admin tiene
    un mensaje recordatorio post-save.

    Validacion previa al guardado:
     - hero.titulo requerido (es el H1)
     - hero.foto requerido (es el LCP element)
     - ctas max 2; cada uno con texto y href no vacios
  -->
  <div class="edit-home container py-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h1 class="heading heading--1 mb-0">Editar Home</h1>
      <b-button :to="{ name: 'AdminHome' }" variant="outline-secondary" size="sm">
        ← Volver
      </b-button>
    </div>

    <b-alert :show="!loaded" variant="info" class="mb-3">
      Cargando configuración actual del home…
    </b-alert>
    <b-alert :show="loadError" variant="warning" class="mb-3" dismissible @dismissed="loadError = false">
      No se pudo cargar el documento <code>siteConfig/home</code>. Si es la primera vez, se va a crear al guardar.
    </b-alert>

    <p class="text-muted small mb-4">
      Los cambios se reflejan en producción tras el próximo rebuild de Vercel (≈ 1-3 min).
    </p>

    <!-- ============ TOP PROMO BAR ============ -->
    <b-card class="mb-3" no-body>
      <b-card-header header-tag="header" class="p-2" role="tab">
        <b-button block v-b-toggle.collapse-topbar variant="light" class="text-left">
          📣 Top Promo Bar
          <b-badge :variant="config.topPromoBar.activo ? 'success' : 'secondary'" class="ml-2">
            {{ config.topPromoBar.activo ? 'Activa' : 'Apagada' }}
          </b-badge>
        </b-button>
      </b-card-header>
      <b-collapse id="collapse-topbar" visible accordion="edit-home-accordion" role="tabpanel">
        <b-card-body>
          <b-form-checkbox v-model="config.topPromoBar.activo" class="mb-3">
            Banner promocional activo
          </b-form-checkbox>

          <b-form-group label="Emoji" label-cols-sm="3">
            <b-form-input v-model="config.topPromoBar.emoji" maxlength="4" placeholder="🍂" />
          </b-form-group>
          <div class="mb-3">
            <I18nField
              label="Texto principal"
              type="text"
              :value-es="i18nVal('topPromoBar.texto', 'es')"
              :value-en="i18nVal('topPromoBar.texto', 'en')"
              :value-pt="i18nVal('topPromoBar.texto', 'pt')"
              :manual-override="i18nOverride('topPromoBar.texto')"
              placeholder="Temporada de otoño · Bosques rojos…"
              @change="(lang, val) => onI18nFieldChange('topPromoBar.texto', lang, val)"
            />
          </div>
          <div class="mb-3">
            <I18nField
              label="Texto del botón CTA"
              type="text"
              :value-es="i18nVal('topPromoBar.ctaTexto', 'es')"
              :value-en="i18nVal('topPromoBar.ctaTexto', 'en')"
              :value-pt="i18nVal('topPromoBar.ctaTexto', 'pt')"
              :manual-override="i18nOverride('topPromoBar.ctaTexto')"
              placeholder="Descubre"
              @change="(lang, val) => onI18nFieldChange('topPromoBar.ctaTexto', lang, val)"
            />
          </div>
          <b-form-group label="Link del CTA" label-cols-sm="3" description="Interno (/landings/otono) o externo (https://…).">
            <b-form-input v-model="config.topPromoBar.ctaHref" placeholder="/landings/otono" />
          </b-form-group>
          <b-form-group label="Color de fondo" label-cols-sm="3">
            <b-form-select v-model="config.topPromoBar.color" :options="colorOptions" />
          </b-form-group>
          <b-form-checkbox v-model="config.topPromoBar.dismissible" class="mb-3">
            Permitir cerrar con ✕
          </b-form-checkbox>
          <b-form-group label="Fecha inicio (opcional)" label-cols-sm="3" description="ISO 8601 (YYYY-MM-DD). Si está vacía, no limita.">
            <b-form-input v-model="config.topPromoBar.fechaInicioStr" type="date" />
          </b-form-group>
          <b-form-group label="Fecha fin (opcional)" label-cols-sm="3">
            <b-form-input v-model="config.topPromoBar.fechaFinStr" type="date" />
          </b-form-group>
        </b-card-body>
      </b-collapse>
    </b-card>

    <!-- ============ HERO ============ -->
    <b-card class="mb-3" no-body>
      <b-card-header header-tag="header" class="p-2" role="tab">
        <b-button block v-b-toggle.collapse-hero variant="light" class="text-left">
          🎯 Hero principal
        </b-button>
      </b-card-header>
      <b-collapse id="collapse-hero" accordion="edit-home-accordion" role="tabpanel">
        <b-card-body>
          <b-form-group label="Tipo de layout" label-cols-sm="3">
            <b-form-select v-model="config.hero.tipo" :options="heroTipoOptions" />
            <small class="text-muted">
              Banner: foto fullwidth + texto centrado encima. Split: foto izq + contenido der.
            </small>
          </b-form-group>
          <b-form-group label="Foto del hero *" label-cols-sm="3" description="Subí una imagen (JPG/PNG/WebP). Se convierte y comprime a WebP en el navegador antes de subir. Recomendado: 1920×800.">
            <div class="d-flex align-items-center mb-2">
              <img
                v-if="config.hero.foto"
                :src="config.hero.foto"
                class="edit-home__preview mr-3"
                alt="preview hero"
              />
              <div class="flex-fill">
                <b-form-file
                  ref="heroFileInput"
                  v-model="heroFile"
                  accept="image/*"
                  :disabled="heroUploading"
                  :placeholder="heroUploading ? 'Subiendo…' : 'Elegir archivo…'"
                  :browse-text="'Buscar foto'"
                  @input="onHeroFileChange"
                />
                <b-progress
                  v-if="heroUploading"
                  :value="heroUploadProgress"
                  variant="info"
                  class="mt-2"
                  height="6px"
                />
                <small v-if="heroUploadError" class="text-danger d-block mt-1">{{ heroUploadError }}</small>
              </div>
            </div>
            <b-form-input
              v-model="config.hero.foto"
              size="sm"
              placeholder="o pegá una URL directamente"
              :disabled="heroUploading"
            />
          </b-form-group>
          <div class="mb-3">
            <I18nField
              label="Alt text — descripción accesible/SEO de la foto"
              type="text"
              :value-es="i18nVal('hero.altText', 'es')"
              :value-en="i18nVal('hero.altText', 'en')"
              :value-pt="i18nVal('hero.altText', 'pt')"
              :manual-override="i18nOverride('hero.altText')"
              placeholder="Surreal Travel · Escapadas en Chile"
              @change="(lang, val) => onI18nFieldChange('hero.altText', lang, val)"
            />
          </div>
          <div class="mb-3">
            <I18nField
              label="Título (H1) * — keyword crítica para SEO"
              type="text"
              :value-es="i18nVal('hero.titulo', 'es')"
              :value-en="i18nVal('hero.titulo', 'en')"
              :value-pt="i18nVal('hero.titulo', 'pt')"
              :manual-override="i18nOverride('hero.titulo')"
              placeholder="Escapadas de naturaleza en Chile"
              @change="(lang, val) => onI18nFieldChange('hero.titulo', lang, val)"
            />
          </div>
          <div class="mb-3">
            <I18nField
              label="Subtítulo"
              type="textarea"
              :rows="2"
              :value-es="i18nVal('hero.subtitulo', 'es')"
              :value-en="i18nVal('hero.subtitulo', 'en')"
              :value-pt="i18nVal('hero.subtitulo', 'pt')"
              :manual-override="i18nOverride('hero.subtitulo')"
              placeholder="Lodges boutique, termas y trekking curados…"
              @change="(lang, val) => onI18nFieldChange('hero.subtitulo', lang, val)"
            />
          </div>
          <div class="mb-3">
            <I18nField
              label="Eyebrow (solo split) — texto pequeño arriba del título"
              type="text"
              :value-es="i18nVal('hero.eyebrow', 'es')"
              :value-en="i18nVal('hero.eyebrow', 'en')"
              :value-pt="i18nVal('hero.eyebrow', 'pt')"
              :manual-override="i18nOverride('hero.eyebrow')"
              placeholder="— Surreal Travel · Chile"
              @change="(lang, val) => onI18nFieldChange('hero.eyebrow', lang, val)"
            />
          </div>

          <hr class="my-4" />
          <div class="d-flex align-items-center justify-content-between mb-2">
            <strong>Botones (CTAs) — máximo 2</strong>
            <b-button
              v-if="config.hero.ctas.length < 2"
              size="sm"
              variant="outline-success"
              @click="addCta"
            >+ Agregar CTA</b-button>
          </div>
          <p class="text-muted small mb-3">
            Si dejas el array vacío, el hero queda sin botones (más limpio para SEO/landings).
          </p>
          <div
            v-for="(cta, idx) in config.hero.ctas"
            :key="'cta-' + idx"
            class="edit-home__cta-row p-3 mb-2 border rounded"
          >
            <div class="d-flex align-items-center justify-content-between mb-2">
              <strong class="small text-muted">Botón #{{ idx + 1 }}</strong>
              <b-button size="sm" variant="outline-danger" @click="removeCta(idx)">Eliminar</b-button>
            </div>
            <b-form-group label="Texto" label-cols-sm="3" class="mb-2">
              <b-form-input v-model="cta.texto" placeholder="Ver escapadas" />
            </b-form-group>
            <b-form-group label="Link" label-cols-sm="3" class="mb-2">
              <b-form-input v-model="cta.href" placeholder="#escapadas o /solicitud o https://…" />
            </b-form-group>
            <b-form-group label="Estilo" label-cols-sm="3" class="mb-0">
              <b-form-select v-model="cta.estilo" :options="ctaEstiloOptions" />
            </b-form-group>
          </div>
        </b-card-body>
      </b-collapse>
    </b-card>

    <!-- ============ SECCIONES ============ -->
    <b-card class="mb-3" no-body>
      <b-card-header header-tag="header" class="p-2" role="tab">
        <b-button block v-b-toggle.collapse-secciones variant="light" class="text-left">
          🏠 Secciones (visibles + orden + textos)
        </b-button>
      </b-card-header>
      <b-collapse id="collapse-secciones" accordion="edit-home-accordion" role="tabpanel">
        <b-card-body>
          <div
            v-for="(s, key) in seccionLabels"
            :key="key"
            class="edit-home__sec-row p-3 mb-2 border rounded"
          >
            <div class="d-flex align-items-center justify-content-between mb-3">
              <strong>{{ s.icon }} {{ s.label }}</strong>
              <b-form-checkbox v-model="config.secciones[key].activo" switch>
                {{ config.secciones[key].activo ? 'Visible' : 'Oculta' }}
              </b-form-checkbox>
            </div>
            <b-form-group label="Orden" label-cols-sm="3" class="mb-2" description="1 = primera después del hero. 5 = última antes del CTA dark.">
              <b-form-input v-model.number="config.secciones[key].orden" type="number" min="1" max="9" />
            </b-form-group>
            <template v-if="key !== 'razones' && key !== 'ctaFinal'">
              <div v-if="config.secciones[key].titulo !== undefined" class="mb-3">
                <I18nField
                  label="Título"
                  type="text"
                  :value-es="i18nVal('secciones.' + key + '.titulo', 'es')"
                  :value-en="i18nVal('secciones.' + key + '.titulo', 'en')"
                  :value-pt="i18nVal('secciones.' + key + '.titulo', 'pt')"
                  :manual-override="i18nOverride('secciones.' + key + '.titulo')"
                  @change="(lang, val) => onI18nFieldChange('secciones.' + key + '.titulo', lang, val)"
                />
              </div>
              <div v-if="config.secciones[key].subtitulo !== undefined" class="mb-3">
                <I18nField
                  label="Subtítulo"
                  type="text"
                  :value-es="i18nVal('secciones.' + key + '.subtitulo', 'es')"
                  :value-en="i18nVal('secciones.' + key + '.subtitulo', 'en')"
                  :value-pt="i18nVal('secciones.' + key + '.subtitulo', 'pt')"
                  :manual-override="i18nOverride('secciones.' + key + '.subtitulo')"
                  @change="(lang, val) => onI18nFieldChange('secciones.' + key + '.subtitulo', lang, val)"
                />
              </div>
              <b-form-group v-if="config.secciones[key].cantidad !== undefined" label="Cantidad" label-cols-sm="3" description="Cuántos items mostrar (solo aplica en modo automático).">
                <b-form-input v-model.number="config.secciones[key].cantidad" type="number" min="1" max="20" />
              </b-form-group>

              <!-- Picker manual de escapadas (masReservadas). Misma logica
                   que el de destinos. Si vacio, el home cae al legacy
                   experienciasImperdibles. Si tildas algo, gana este. -->
              <template v-if="key === 'masReservadas'">
                <hr class="my-3" />
                <strong class="d-block mb-2">Selección manual de escapadas</strong>
                <p class="text-muted small mb-2">
                  Tildá las escapadas que querés mostrar. Si dejás todo vacío,
                  se usa la lista legacy "Experiencias Imperdibles"
                  (primeras {{ config.secciones.masReservadas.cantidad || 6 }}).
                </p>
                <div v-if="!experienciasLoaded" class="text-muted small">Cargando escapadas…</div>
                <template v-else>
                  <div
                    v-if="config.secciones.masReservadas.slugs && config.secciones.masReservadas.slugs.length"
                    class="mb-3 p-2 bg-light rounded"
                  >
                    <small class="text-success font-weight-bold">
                      Orden actual ({{ config.secciones.masReservadas.slugs.length }} seleccionadas):
                    </small>
                    <ol class="mb-0 pl-4 small">
                      <li
                        v-for="(slug, sidx) in config.secciones.masReservadas.slugs"
                        :key="'sel-exp-' + slug"
                      >
                        {{ experienciaNameBySlug(slug) || slug }}
                        <button
                          type="button"
                          class="btn btn-link btn-sm p-0 ml-1"
                          :disabled="sidx === 0"
                          @click="moveExperienciaSlug(sidx, -1)"
                        >↑</button>
                        <button
                          type="button"
                          class="btn btn-link btn-sm p-0"
                          :disabled="sidx === config.secciones.masReservadas.slugs.length - 1"
                          @click="moveExperienciaSlug(sidx, 1)"
                        >↓</button>
                      </li>
                    </ol>
                  </div>
                  <b-form-checkbox-group
                    v-model="config.secciones.masReservadas.slugs"
                    stacked
                  >
                    <b-form-checkbox
                      v-for="e in availableExperiencias"
                      :key="e.slug"
                      :value="e.slug"
                    >
                      {{ e.titulo }}
                      <small class="text-muted">— {{ e.slug }}</small>
                    </b-form-checkbox>
                  </b-form-checkbox-group>
                </template>
              </template>

              <!-- Picker manual de destinos. Si dejas todo destildado, el
                   home cae al modo auto (primeros N destinos visibles).
                   Si tildas alguno, solo esos se muestran en el orden de
                   la lista — Pablo reordena con ↑↓. -->
              <template v-if="key === 'destinos'">
                <hr class="my-3" />
                <strong class="d-block mb-2">Selección manual de destinos</strong>
                <p class="text-muted small mb-2">
                  Tildá los destinos que querés mostrar. Si dejás todo vacío
                  se muestran automáticamente los primeros
                  {{ config.secciones.destinos.cantidad || 8 }} visibles.
                </p>
                <div v-if="!destinosLoaded" class="text-muted small">Cargando destinos…</div>
                <template v-else>
                  <div
                    v-if="config.secciones.destinos.slugs && config.secciones.destinos.slugs.length"
                    class="mb-3 p-2 bg-light rounded"
                  >
                    <small class="text-success font-weight-bold">
                      Orden actual ({{ config.secciones.destinos.slugs.length }} seleccionados):
                    </small>
                    <ol class="mb-0 pl-4 small">
                      <li
                        v-for="(slug, sidx) in config.secciones.destinos.slugs"
                        :key="'sel-' + slug"
                      >
                        {{ destinoNameBySlug(slug) || slug }}
                        <button
                          type="button"
                          class="btn btn-link btn-sm p-0 ml-1"
                          :disabled="sidx === 0"
                          @click="moveDestinoSlug(sidx, -1)"
                        >↑</button>
                        <button
                          type="button"
                          class="btn btn-link btn-sm p-0"
                          :disabled="sidx === config.secciones.destinos.slugs.length - 1"
                          @click="moveDestinoSlug(sidx, 1)"
                        >↓</button>
                      </li>
                    </ol>
                  </div>
                  <b-form-checkbox-group
                    v-model="config.secciones.destinos.slugs"
                    stacked
                  >
                    <b-form-checkbox
                      v-for="d in availableDestinos"
                      :key="d.slug"
                      :value="d.slug"
                    >
                      {{ d.titulo }}
                      <small class="text-muted">— {{ d.slug }}</small>
                    </b-form-checkbox>
                  </b-form-checkbox-group>
                </template>
              </template>
            </template>

            <!-- Subeditor: razones (3 items con icon + titulo + texto) -->
            <template v-if="key === 'razones'">
              <div
                v-for="(item, ridx) in config.secciones.razones.items"
                :key="'r-' + ridx"
                class="edit-home__razon p-2 mb-2 border rounded bg-light"
              >
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <small class="text-muted">Razón #{{ ridx + 1 }}</small>
                  <b-button v-if="config.secciones.razones.items.length > 1" size="sm" variant="outline-danger" @click="removeRazon(ridx)">×</b-button>
                </div>
                <b-form-group label="Icono" label-cols-sm="3" class="mb-2">
                  <b-form-input v-model="item.icon" maxlength="4" placeholder="✨" />
                </b-form-group>
                <b-form-group label="Título" label-cols-sm="3" class="mb-2">
                  <b-form-input v-model="item.titulo" />
                </b-form-group>
                <b-form-group label="Texto" label-cols-sm="3" class="mb-0">
                  <b-form-textarea v-model="item.texto" rows="2" />
                </b-form-group>
              </div>
              <b-button v-if="config.secciones.razones.items.length < 6" size="sm" variant="outline-success" @click="addRazon">+ Agregar razón</b-button>
            </template>

            <!-- Subeditor: ctaFinal -->
            <template v-if="key === 'ctaFinal'">
              <div class="mb-3">
                <I18nField
                  label="Título"
                  type="text"
                  :value-es="i18nVal('secciones.ctaFinal.titulo', 'es')"
                  :value-en="i18nVal('secciones.ctaFinal.titulo', 'en')"
                  :value-pt="i18nVal('secciones.ctaFinal.titulo', 'pt')"
                  :manual-override="i18nOverride('secciones.ctaFinal.titulo')"
                  @change="(lang, val) => onI18nFieldChange('secciones.ctaFinal.titulo', lang, val)"
                />
              </div>
              <div class="mb-3">
                <I18nField
                  label="Descripción"
                  type="textarea"
                  :rows="2"
                  :value-es="i18nVal('secciones.ctaFinal.descripcion', 'es')"
                  :value-en="i18nVal('secciones.ctaFinal.descripcion', 'en')"
                  :value-pt="i18nVal('secciones.ctaFinal.descripcion', 'pt')"
                  :manual-override="i18nOverride('secciones.ctaFinal.descripcion')"
                  @change="(lang, val) => onI18nFieldChange('secciones.ctaFinal.descripcion', lang, val)"
                />
              </div>
              <b-form-group label="Botón 1 — texto" label-cols-sm="3" class="mb-2">
                <b-form-input v-model="config.secciones.ctaFinal.btn1.texto" />
              </b-form-group>
              <b-form-group label="Botón 1 — link" label-cols-sm="3" class="mb-2">
                <b-form-input v-model="config.secciones.ctaFinal.btn1.href" />
              </b-form-group>
              <b-form-group label="Botón 2 — texto" label-cols-sm="3" class="mb-2">
                <b-form-input v-model="config.secciones.ctaFinal.btn2.texto" />
              </b-form-group>
              <b-form-group label="Botón 2 — link" label-cols-sm="3" class="mb-0">
                <b-form-input v-model="config.secciones.ctaFinal.btn2.href" />
              </b-form-group>
            </template>
          </div>
        </b-card-body>
      </b-collapse>
    </b-card>

    <!-- ============ SEO ============ -->
    <b-card class="mb-5" no-body>
      <b-card-header header-tag="header" class="p-2" role="tab">
        <b-button block v-b-toggle.collapse-seo variant="light" class="text-left">
          🔍 SEO (meta para WhatsApp / Facebook / Twitter)
        </b-button>
      </b-card-header>
      <b-collapse id="collapse-seo" accordion="edit-home-accordion" role="tabpanel">
        <b-card-body>
          <div class="mb-3">
            <I18nField
              label="OG Título — aparece al compartir el link en WhatsApp / Facebook"
              type="text"
              :value-es="i18nVal('seo.ogTitulo', 'es')"
              :value-en="i18nVal('seo.ogTitulo', 'en')"
              :value-pt="i18nVal('seo.ogTitulo', 'pt')"
              :manual-override="i18nOverride('seo.ogTitulo')"
              @change="(lang, val) => onI18nFieldChange('seo.ogTitulo', lang, val)"
            />
          </div>
          <div class="mb-3">
            <I18nField
              label="OG Descripción — texto bajo el título en el preview"
              type="textarea"
              :rows="2"
              :value-es="i18nVal('seo.ogDescripcion', 'es')"
              :value-en="i18nVal('seo.ogDescripcion', 'en')"
              :value-pt="i18nVal('seo.ogDescripcion', 'pt')"
              :manual-override="i18nOverride('seo.ogDescripcion')"
              @change="(lang, val) => onI18nFieldChange('seo.ogDescripcion', lang, val)"
            />
          </div>
          <b-form-group label="OG Imagen" label-cols-sm="3" description="Imagen para preview en WhatsApp/FB. 1200×630 ideal. Si vacío usa la foto del hero.">
            <div class="d-flex align-items-center mb-2">
              <img
                v-if="config.seo.ogImagen"
                :src="config.seo.ogImagen"
                class="edit-home__preview mr-3"
                alt="preview og"
              />
              <div class="flex-fill">
                <b-form-file
                  v-model="ogFile"
                  accept="image/*"
                  :disabled="ogUploading"
                  :placeholder="ogUploading ? 'Subiendo…' : 'Elegir archivo…'"
                  :browse-text="'Buscar foto'"
                  @input="onOgFileChange"
                />
                <b-progress
                  v-if="ogUploading"
                  :value="ogUploadProgress"
                  variant="info"
                  class="mt-2"
                  height="6px"
                />
                <small v-if="ogUploadError" class="text-danger d-block mt-1">{{ ogUploadError }}</small>
              </div>
            </div>
            <b-form-input
              v-model="config.seo.ogImagen"
              size="sm"
              placeholder="o pegá una URL directamente"
              :disabled="ogUploading"
            />
          </b-form-group>
        </b-card-body>
      </b-collapse>
    </b-card>

    <!-- ============ SAVE BAR sticky ============ -->
    <div class="edit-home__save-bar">
      <div v-if="errors.length" class="edit-home__errors mb-2">
        <small v-for="(err, i) in errors" :key="i" class="text-danger d-block">
          ⚠ {{ err }}
        </small>
      </div>
      <div v-if="successMsg" class="edit-home__success mb-2">
        <small class="text-success d-block">✓ {{ successMsg }}</small>
      </div>
      <b-button variant="primary" :disabled="saving" @click="save">
        {{ saving ? 'Guardando…' : 'Guardar cambios' }}
      </b-button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { db, storage } from '../../firebase.service';
import { optimizeImage } from '@/store/utilities/imageOptimizer';
import I18nField from '@/components/I18nField.vue';

// Default object usado al inicializar el form y como fallback cuando el
// doc no existe todavia. Espejo exacto del defaultHome() del sitio publico
// (store/modules/siteConfig.js) — si cambia uno cambia el otro.
const makeDefault = () => ({
  topPromoBar: {
    activo: false,
    emoji: '🍂',
    texto: 'Temporada de otoño · Bosques rojos, termas humeantes, precios bajos',
    ctaTexto: 'Descubre',
    ctaHref: '/landings/otono',
    dismissible: true,
    fechaInicio: null,
    fechaFin: null,
    fechaInicioStr: '', // helper para input type=date (string ISO YYYY-MM-DD)
    fechaFinStr: '',
    color: 'dark',
  },
  hero: {
    tipo: 'banner',
    foto: '/img_hero.webp',
    titulo: 'Escapadas de naturaleza en Chile · Conguillío, Pucón y más',
    subtitulo: 'Lodges boutique, termas y trekking curados de norte a sur.',
    eyebrow: '',
    altText: 'Surreal Travel · Escapadas en Chile',
    ctas: [
      { texto: 'Ver escapadas', href: '#escapadas', estilo: 'brand' },
      { texto: 'Personaliza tu viaje', href: '/solicitud', estilo: 'outline' },
    ],
  },
  secciones: {
    masReservadas: {
      activo: true,
      titulo: 'Las más reservadas este mes',
      subtitulo: 'Lo que otros viajeros eligieron',
      orden: 1,
      cantidad: 6,
      // Seleccion manual: array de slugs de experiencia en orden. Si vacio,
      // el home cae al legacy experienciasImperdibles.
      slugs: [],
    },
    destinos: {
      activo: true,
      titulo: 'Nuestros destinos',
      subtitulo: 'Elige el destino y descubre las escapadas curadas para ti',
      orden: 2,
      cantidad: 8,
      // Seleccion manual: array de slugs de destino en orden de
      // exhibicion. Si vacio, el home cae al auto-mode.
      slugs: [],
    },
    reviews: {
      activo: true,
      titulo: 'Lo que dicen viajeros como tú',
      subtitulo: '+5.000 personas confiaron en Surreal Travel para vivir su escapada',
      orden: 3,
      fuente: 'curados-admin',
      cantidad: 4,
      items: [],
    },
    razones: {
      activo: true,
      orden: 4,
      items: [
        { icon: '✨', titulo: '100% curado', texto: 'Visitamos cada lodge antes de recomendarlo.' },
        { icon: '🤝', titulo: 'Operadores locales', texto: 'Trabajamos con familias y guías de la zona.' },
        { icon: '⚡', titulo: 'Reserva en 24h', texto: 'Confirmamos disponibilidad rápidamente.' },
      ],
    },
    ctaFinal: {
      activo: true,
      orden: 5,
      titulo: '¿No encuentras lo que buscas?',
      descripcion: 'Diseñamos escapadas a medida — cuéntanos qué tienes en mente.',
      btn1: { texto: 'Personaliza tu viaje', href: '/solicitud' },
      btn2: { texto: 'WhatsApp', href: 'https://wa.me/56952068914' },
    },
  },
  seo: {
    ogTitulo: 'Surreal Travel · Escapadas de naturaleza en Chile',
    ogDescripcion: 'Lodges boutique, termas y trekking curados de norte a sur. Reserva en 24h.',
    ogImagen: '/img_hero.webp',
  },
  // i18n: ramas paralelas para EN/PT. La Cloud Function las puebla via
  // DeepL al detectar cambios en el contenido ES. Si Pablo edita una
  // traduccion manual, manualOverride[path][lang] = true y la function
  // ya no la pisa.
  ingles: {},
  portugues: {},
  manualOverride: {},
});

export default {
  name: 'EditHome',
  components: { I18nField },
  data() {
    return {
      config: makeDefault(),
      loaded: false,
      loadError: false,
      saving: false,
      errors: [],
      successMsg: '',
      // Estado de upload del hero foto. heroFile es el File seleccionado en
      // <b-form-file>; al onInput disparamos optimizeImage + upload a
      // Firebase Storage, y al terminar pegamos la URL en config.hero.foto.
      heroFile: null,
      heroUploading: false,
      heroUploadProgress: 0,
      heroUploadError: '',
      ogFile: null,
      ogUploading: false,
      ogUploadProgress: 0,
      ogUploadError: '',
      // Listado de destinos para poblar el picker manual de la seccion
      // "Nuestros destinos". Se carga en mounted(). availableDestinos es
      // {slug, titulo}[].
      availableDestinos: [],
      destinosLoaded: false,
      availableExperiencias: [],
      experienciasLoaded: false,
      // Labels y orden de despliegue de las secciones en el panel.
      seccionLabels: {
        masReservadas: { icon: '⭐', label: 'Las más reservadas' },
        destinos: { icon: '📍', label: 'Nuestros destinos' },
        reviews: { icon: '💬', label: 'Reseñas / testimonios' },
        razones: { icon: '✨', label: 'Por qué Surreal (3 razones)' },
        ctaFinal: { icon: '📞', label: 'CTA dark final' },
      },
      heroTipoOptions: [
        { value: 'banner', text: 'Banner (foto full + texto centrado encima)' },
        { value: 'split', text: 'Split (foto izquierda + contenido a la derecha)' },
      ],
      colorOptions: [
        { value: 'dark', text: 'Oscuro' },
        { value: 'brand', text: 'Azul brand' },
        { value: 'accent', text: 'Amarillo accent' },
      ],
      ctaEstiloOptions: [
        { value: 'brand', text: 'Azul brand (relleno)' },
        { value: 'accent', text: 'Amarillo accent (relleno)' },
        { value: 'outline', text: 'Outline (transparente con borde)' },
      ],
    };
  },
  methods: {
    // ===== Helpers i18n =====
    // Lee/escribe paths root-relative (ej. 'hero.titulo'). ES vive en
    // config.{path}, EN en config.ingles.{path}, PT en config.portugues.{path}.
    // Mantiene config.manualOverride[path] = { en, pt } cuando Pablo
    // edita una traduccion manual.
    i18nGetDeep(obj, path) {
      if (obj == null || !path) return undefined;
      return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
    },
    i18nSetDeep(obj, path, value) {
      const parts = path.split('.');
      let cur = obj;
      for (let i = 0; i < parts.length - 1; i++) {
        const k = parts[i];
        if (cur[k] == null || typeof cur[k] !== 'object') {
          // Vue.set para reactividad sobre props nuevas.
          Vue.set(cur, k, {});
        }
        cur = cur[k];
      }
      Vue.set(cur, parts[parts.length - 1], value);
    },
    i18nVal(path, lang) {
      if (lang === 'es') return this.i18nGetDeep(this.config, path);
      const branch = lang === 'en' ? 'ingles' : 'portugues';
      return this.i18nGetDeep(this.config[branch] || {}, path);
    },
    i18nOverride(path) {
      const ov = (this.config.manualOverride || {})[path] || {};
      return { en: !!ov.en, pt: !!ov.pt };
    },
    onI18nFieldChange(path, lang, value) {
      if (lang === 'es') {
        this.i18nSetDeep(this.config, path, value);
        return;
      }
      const branch = lang === 'en' ? 'ingles' : 'portugues';
      if (!this.config[branch]) Vue.set(this.config, branch, {});
      this.i18nSetDeep(this.config[branch], path, value);
      // Marcar manualOverride para que la Cloud Function no pise esta edicion.
      if (!this.config.manualOverride) Vue.set(this.config, 'manualOverride', {});
      if (!this.config.manualOverride[path]) Vue.set(this.config.manualOverride, path, { en: false, pt: false });
      Vue.set(this.config.manualOverride[path], lang, true);
    },

    // ===== UPLOADS DE IMAGEN =====
    // Pipeline:
    //   1. optimizeImage convierte JPG/PNG a WebP y resize a maxWidth (1600
    //      para hero — la foto se muestra hasta 1920px en desktop pero el
    //      assets ya viene comprimido). Si el archivo ya es WebP, lo deja igual.
    //   2. Subimos a Firebase Storage a un path estable por destino del archivo.
    //   3. Pegamos la getDownloadURL en el campo correspondiente.
    // Mismo patron que EditorMediosGaleria.vue.
    async uploadHelper({ file, basePath, maxWidth, onProgress }) {
      const opt = await optimizeImage(file, { maxWidth, quality: 0.85 });
      if (!opt || !opt.blob) throw new Error('No se pudo optimizar la imagen');
      const fileName = Math.floor(Math.random() * 100000000) + opt.extension;
      const path = `${basePath}/${fileName}`;
      const ref = storage.ref().child(path);
      const task = ref.put(opt.blob, { contentType: opt.blob.type || 'image/webp' });
      await new Promise((resolve, reject) => {
        task.on('state_changed',
          (snap) => {
            const pct = snap.totalBytes > 0 ? (snap.bytesTransferred / snap.totalBytes) * 100 : 0;
            if (onProgress) onProgress(pct);
          },
          (err) => reject(err),
          () => resolve(),
        );
      });
      return await task.snapshot.ref.getDownloadURL();
    },
    async onHeroFileChange(file) {
      if (!file) return;
      this.heroUploading = true;
      this.heroUploadProgress = 0;
      this.heroUploadError = '';
      try {
        const url = await this.uploadHelper({
          file,
          basePath: 'siteConfig/home/hero',
          maxWidth: 1920, // hero full-bleed puede ser >1600 en monitores 4K
          onProgress: (p) => { this.heroUploadProgress = p; },
        });
        this.config.hero.foto = url;
        this.heroFile = null; // reset el input
      } catch (e) {
        this.heroUploadError = (e && e.message) || String(e);
      } finally {
        this.heroUploading = false;
      }
    },
    async onOgFileChange(file) {
      if (!file) return;
      this.ogUploading = true;
      this.ogUploadProgress = 0;
      this.ogUploadError = '';
      try {
        const url = await this.uploadHelper({
          file,
          basePath: 'siteConfig/home/og',
          maxWidth: 1200, // 1200x630 es el target OG, no necesitamos mas
          onProgress: (p) => { this.ogUploadProgress = p; },
        });
        this.config.seo.ogImagen = url;
        this.ogFile = null;
      } catch (e) {
        this.ogUploadError = (e && e.message) || String(e);
      } finally {
        this.ogUploading = false;
      }
    },
    // ===== Picker manual de destinos =====
    async loadDestinos() {
      try {
        const snap = await db.collection('destinos').get();
        const arr = [];
        snap.forEach((doc) => {
          const d = doc.data();
          if (!d || !d.slug) return;
          // Mostramos todos los destinos en el picker, incluso ocultos —
          // si Pablo quiere mostrar uno "oculto" como destacado, que sepa
          // que existe. El sitio publico igual respeta oculto !== true.
          arr.push({ slug: d.slug, titulo: d.titulo || d.slug, oculto: !!d.oculto });
        });
        arr.sort((a, b) => (a.titulo || '').localeCompare(b.titulo || ''));
        this.availableDestinos = arr;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[EditHome] loadDestinos error', e);
      } finally {
        this.destinosLoaded = true;
      }
    },
    destinoNameBySlug(slug) {
      const d = this.availableDestinos.find((x) => x.slug === slug);
      return d ? d.titulo : '';
    },
    moveDestinoSlug(idx, dir) {
      const arr = this.config.secciones.destinos.slugs;
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return;
      const [removed] = arr.splice(idx, 1);
      arr.splice(target, 0, removed);
    },
    // ===== Picker manual de experiencias (masReservadas) =====
    async loadExperiencias() {
      try {
        const snap = await db.collection('experiencias').get();
        const arr = [];
        snap.forEach((doc) => {
          const e = doc.data();
          if (!e || !e.slug) return;
          arr.push({ slug: e.slug, titulo: e.titulo || e.slug, oculto: !!e.oculto });
        });
        arr.sort((a, b) => (a.titulo || '').localeCompare(b.titulo || ''));
        this.availableExperiencias = arr;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[EditHome] loadExperiencias error', e);
      } finally {
        this.experienciasLoaded = true;
      }
    },
    experienciaNameBySlug(slug) {
      const e = this.availableExperiencias.find((x) => x.slug === slug);
      return e ? e.titulo : '';
    },
    moveExperienciaSlug(idx, dir) {
      const arr = this.config.secciones.masReservadas.slugs;
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return;
      const [removed] = arr.splice(idx, 1);
      arr.splice(target, 0, removed);
    },
    addCta() {
      if (this.config.hero.ctas.length >= 2) return;
      this.config.hero.ctas.push({ texto: '', href: '', estilo: 'brand' });
    },
    removeCta(idx) {
      this.config.hero.ctas.splice(idx, 1);
    },
    addRazon() {
      this.config.secciones.razones.items.push({ icon: '✨', titulo: '', texto: '' });
    },
    removeRazon(idx) {
      this.config.secciones.razones.items.splice(idx, 1);
    },

    // Convierte string YYYY-MM-DD a ISO ms (al inicio del dia, UTC).
    dateStrToMs(s) {
      if (!s || typeof s !== 'string') return null;
      const t = Date.parse(s);
      return Number.isFinite(t) ? t : null;
    },
    // Inversa — de un valor del doc (ms o Firestore timestamp) a string YYYY-MM-DD.
    msToDateStr(v) {
      if (!v) return '';
      let ms;
      if (typeof v === 'number') ms = v;
      else if (v && typeof v.seconds === 'number') ms = v.seconds * 1000;
      else if (typeof v === 'string') { const t = Date.parse(v); ms = Number.isFinite(t) ? t : null; }
      if (!ms) return '';
      return new Date(ms).toISOString().slice(0, 10);
    },

    validate() {
      const errs = [];
      const h = this.config.hero || {};
      if (!h.titulo || !String(h.titulo).trim()) errs.push('El título del hero es requerido (es el H1 para SEO).');
      if (!h.foto || !String(h.foto).trim()) errs.push('La foto del hero es requerida.');
      const ctas = Array.isArray(h.ctas) ? h.ctas : [];
      if (ctas.length > 2) errs.push('No puedes tener más de 2 botones (CTAs) en el hero.');
      ctas.forEach((c, idx) => {
        if (!c.texto || !String(c.texto).trim()) errs.push(`CTA #${idx + 1}: falta el texto.`);
        if (!c.href || !String(c.href).trim()) errs.push(`CTA #${idx + 1}: falta el link.`);
      });
      // Si la promo bar esta activa, exigimos al menos un texto.
      if (this.config.topPromoBar.activo && !String(this.config.topPromoBar.texto || '').trim()) {
        errs.push('Si la Top Promo Bar está activa, el texto es requerido.');
      }
      this.errors = errs;
      return errs.length === 0;
    },

    async save() {
      this.successMsg = '';
      if (!this.validate()) return;
      this.saving = true;
      try {
        // Convertir las fechas helper a timestamps reales antes de guardar.
        // El sitio publico lee ms o Firestore.Timestamp indistintamente.
        const payload = JSON.parse(JSON.stringify(this.config));
        payload.topPromoBar.fechaInicio = this.dateStrToMs(this.config.topPromoBar.fechaInicioStr) || null;
        payload.topPromoBar.fechaFin = this.dateStrToMs(this.config.topPromoBar.fechaFinStr) || null;
        // Removemos los helpers que no van al doc (no tienen sentido en Firestore).
        delete payload.topPromoBar.fechaInicioStr;
        delete payload.topPromoBar.fechaFinStr;

        await db.collection('siteConfig').doc('home').set(payload, { merge: false });
        this.successMsg = 'Guardado. Los cambios aparecen en producción tras el próximo rebuild (1-3 min).';
        setTimeout(() => { this.successMsg = ''; }, 6000);
      } catch (e) {
        this.errors = ['Error al guardar: ' + (e && e.message ? e.message : String(e))];
        // eslint-disable-next-line no-console
        console.error('[EditHome] save error', e);
      } finally {
        this.saving = false;
      }
    },

    async load() {
      try {
        const snap = await db.collection('siteConfig').doc('home').get();
        if (snap.exists) {
          const data = snap.data() || {};
          // Merge defensivo con defaults (igual logica que el store del sitio).
          const def = makeDefault();
          this.config = {
            topPromoBar: { ...def.topPromoBar, ...(data.topPromoBar || {}) },
            hero: {
              ...def.hero,
              ...(data.hero || {}),
              // Si el doc trae ctas, usarlas; sino dejar los defaults.
              ctas: Array.isArray(data.hero && data.hero.ctas) ? data.hero.ctas : def.hero.ctas,
            },
            secciones: {
              masReservadas: {
                ...def.secciones.masReservadas,
                ...((data.secciones || {}).masReservadas || {}),
                slugs: Array.isArray(((data.secciones || {}).masReservadas || {}).slugs)
                  ? data.secciones.masReservadas.slugs
                  : [],
              },
              destinos: {
                ...def.secciones.destinos,
                ...((data.secciones || {}).destinos || {}),
                // Coerce slugs a array por si el doc viene con null/undefined
                slugs: Array.isArray(((data.secciones || {}).destinos || {}).slugs)
                  ? data.secciones.destinos.slugs
                  : [],
              },
              reviews: {
                ...def.secciones.reviews,
                ...((data.secciones || {}).reviews || {}),
                items: Array.isArray(((data.secciones || {}).reviews || {}).items)
                  ? data.secciones.reviews.items
                  : def.secciones.reviews.items,
              },
              razones: {
                ...def.secciones.razones,
                ...((data.secciones || {}).razones || {}),
                items: Array.isArray(((data.secciones || {}).razones || {}).items)
                  ? data.secciones.razones.items
                  : def.secciones.razones.items,
              },
              ctaFinal: {
                ...def.secciones.ctaFinal,
                ...((data.secciones || {}).ctaFinal || {}),
                btn1: { ...def.secciones.ctaFinal.btn1, ...(((data.secciones || {}).ctaFinal || {}).btn1 || {}) },
                btn2: { ...def.secciones.ctaFinal.btn2, ...(((data.secciones || {}).ctaFinal || {}).btn2 || {}) },
              },
            },
            seo: { ...def.seo, ...(data.seo || {}) },
            // Ramas i18n: las preservamos tal cual del doc. La Cloud
            // Function las pobla; el admin las lee/escribe via los helpers
            // i18nVal/onI18nFieldChange.
            ingles: data.ingles || {},
            portugues: data.portugues || {},
            manualOverride: data.manualOverride || {},
          };
          // Hidratar helpers de fecha string para los inputs type=date.
          this.config.topPromoBar.fechaInicioStr = this.msToDateStr(this.config.topPromoBar.fechaInicio);
          this.config.topPromoBar.fechaFinStr = this.msToDateStr(this.config.topPromoBar.fechaFin);
        }
      } catch (e) {
        this.loadError = true;
        // eslint-disable-next-line no-console
        console.warn('[EditHome] load error', e);
      } finally {
        this.loaded = true;
      }
    },
  },
  mounted() {
    this.load();
    this.loadDestinos();
    this.loadExperiencias();
  },
};
</script>

<style scoped>
.edit-home {
  max-width: 920px;
  padding-bottom: 120px; /* deja espacio para la save bar sticky */
}

.edit-home__save-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #e5e5e5;
  padding: 14px 16px;
  z-index: 100;
  text-align: center;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
}

.edit-home__errors {
  max-width: 920px;
  margin: 0 auto;
}
.edit-home__success {
  max-width: 920px;
  margin: 0 auto;
}

.edit-home__cta-row {
  background: #fafafa;
}

.edit-home__razon {
  background: #f9f9f9;
}

.edit-home__sec-row {
  background: #fff;
}

.edit-home__preview {
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e5e5e5;
  flex-shrink: 0;
  background: #f6f6f6;
}
</style>
